/**
 * ERPNext API Integration Service
 *
 * Fetches products, inventory, and pricing from ERPNext backend.
 * For development: connects to local ERPNext (http://localhost:8000)
 * For production: connects through Cloudflare Workers proxy
 */

import type { Product } from '@/types/product';

// In development, Vite proxies /api to ERPNext (localhost:8000)
// In production, use environment variable or proxy through Cloudflare Workers
const ERPNEXT_URL = import.meta.env.VITE_ERPNEXT_URL || '';
// Unused for now - session-based auth is used instead
// const ERPNEXT_API_KEY = import.meta.env.VITE_ERPNEXT_API_KEY || '';
// const ERPNEXT_API_SECRET = import.meta.env.VITE_ERPNEXT_API_SECRET || '';

interface ERPNextItem {
  name: string;
  item_code: string;
  item_name: string;
  item_group: string;
  description: string;
  standard_rate: number;
  stock_uom: string;
  image?: string;
  custom_features?: string;
  custom_specifications?: string;
  custom_rating_average?: number;
  custom_rating_count?: number;
  custom_allow_preorder?: number;
  custom_preorder_lead_time_weeks?: string;
  custom_badge?: string;
  custom_is_featured?: number;
  custom_is_new?: number;
}

// Unused for now - keeping for future reference
// interface ERPNextItemPrice {
//   price_list: string;
//   currency: string;
//   price_list_rate: number;
// }

// Unused for now - keeping for future reference
// interface ERPNextStockBalance {
//   actual_qty: number;
//   reserved_qty: number;
//   available_qty: number;
// }

/**
 * Test ERPNext API connectivity - Unused for now
 */
// async function testConnection(): Promise<void> {
//   const baseUrl = ERPNEXT_URL || '';
//
//   try {
//     const testResponse = await fetch(`${baseUrl}/api/resource/Item?limit_page_length=1`, {
//       credentials: 'include', // Include cookies for session
//     });
//
//     console.log('ERPNext API connection test:', testResponse.status);
//
//     if (!testResponse.ok) {
//       console.warn('ERPNext API returned non-OK status. Items may require authentication or "Show in Website" to be enabled.');
//     }
//   } catch (error) {
//     console.error('ERPNext API connection failed:', error);
//   }
// }

/**
 * Fetch all sellable items from ERPNext
 */
export async function fetchProducts(): Promise<Product[]> {
  try {
    const baseUrl = ERPNEXT_URL || ''; // Empty in dev (use relative URL for proxy)
    const response = await fetch(
      `${baseUrl}/api/resource/Item?` +
        new URLSearchParams({
          fields: JSON.stringify([
            'name',
            'item_code',
            'item_name',
            'item_group',
            'description',
            'standard_rate',
            'stock_uom',
            'image',
          ]),
          filters: JSON.stringify([['is_sales_item', '=', 1]]),
          limit_page_length: '999',
        }),
      {
        credentials: 'include',
      }
    );

    if (!response.ok) {
      throw new Error(`ERPNext API error: ${response.statusText}`);
    }

    const data = await response.json();
    const items: ERPNextItem[] = data.data || [];

    // Transform ERPNext items to Product format
    const products = await Promise.all(
      items.map(async (item) => transformERPNextItemToProduct(item))
    );

    return products;
  } catch (error) {
    console.error('Failed to fetch products from ERPNext:', error);
    throw error;
  }
}

/**
 * Fetch multi-currency prices for an item - Unused, using hardcoded conversions instead
 */
// async function fetchItemPrices(itemCode: string): Promise<Record<string, number>> {
//   try {
//     const baseUrl = ERPNEXT_URL || '';
//     const response = await fetch(
//       `${baseUrl}/api/resource/Item Price?` +
//         new URLSearchParams({
//           fields: JSON.stringify(['price_list', 'currency', 'price_list_rate']),
//           filters: JSON.stringify([['item_code', '=', itemCode]]),
//         }),
//       {
//         credentials: 'include',
//       }
//     );
//
//     if (!response.ok) {
//       return {};
//     }
//
//     const data = await response.json();
//     const prices: ERPNextItemPrice[] = data.data || [];
//
//     // Convert to { CAD: 176, USD: 130, ... } format
//     const priceMap: Record<string, number> = {};
//     prices.forEach((price) => {
//       priceMap[price.currency] = price.price_list_rate;
//     });
//
//     return priceMap;
//   } catch (error) {
//     console.error(`Failed to fetch prices for ${itemCode}:`, error);
//     return {};
//   }
// }

/**
 * Fetch real-time stock level for an item
 */
async function fetchStockLevel(itemCode: string): Promise<number> {
  try {
    const baseUrl = ERPNEXT_URL || '';
    const response = await fetch(
      `${baseUrl}/api/resource/Bin?` +
        new URLSearchParams({
          fields: JSON.stringify(['actual_qty', 'reserved_qty']),
          filters: JSON.stringify([['item_code', '=', itemCode]]),
        }),
      {
        credentials: 'include',
      }
    );

    if (!response.ok) {
      return 0;
    }

    const data = await response.json();
    const bins = data.data || [];

    // Sum up stock across all warehouses
    const totalStock = bins.reduce(
      (sum: number, bin: any) => sum + (bin.actual_qty || 0),
      0
    );

    return totalStock;
  } catch (error) {
    console.error(`Failed to fetch stock for ${itemCode}:`, error);
    return 0;
  }
}

/**
 * Transform ERPNext Item to Product interface
 */
async function transformERPNextItemToProduct(item: ERPNextItem): Promise<Product> {
  // Fetch stock level
  const stockQty = await fetchStockLevel(item.item_code);

  // Determine stock status (no custom preorder field, use stock-based logic)
  let stockStatus: 'in_stock' | 'low_stock' | 'out_of_stock' | 'preorder' = 'out_of_stock';
  if (stockQty > 10) {
    stockStatus = 'in_stock';
  } else if (stockQty > 0) {
    stockStatus = 'low_stock';
  }

  // Generate slug from item name
  const slug = item.item_name.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');

  // Determine category and subcategory
  const category = (item.item_group === 'PadBox' || item.item_group === 'Hing3DBox' || item.item_group === 'Accessories')
    ? item.item_group
    : 'Accessories' as const;
  const subcategory = 'Available';

  // Build prices with all required currencies
  const productPrices = {
    CAD: item.standard_rate,
    USD: item.standard_rate * 0.73,  // Approximate conversion
    AUD: item.standard_rate * 1.11,
    EUR: item.standard_rate * 0.68,
    GBP: item.standard_rate * 0.58,
    AED: item.standard_rate * 2.68,
  };

  return {
    id: `erpnext_${item.item_code}`,
    name: item.item_name,
    slug,
    description: item.description || '',
    images: item.image ? [item.image] : [],
    category,
    subcategory,
    sku: item.item_code,
    prices: productPrices,
    stockStatus,
    preorderShippingWeeks: undefined,
    rating: {
      average: 0,
      count: 0,
    },
    features: [],
    specifications: {},
    isFeatured: false,
    isNew: false,
    badge: undefined,
  };
}

/**
 * Get authentication headers for ERPNext API - Unused for now
 */
// function getAuthHeaders(): HeadersInit {
//   // For development with session-based auth
//   if (import.meta.env.DEV) {
//     return {
//       'Content-Type': 'application/json',
//     };
//   }
//
//   // For production with API token
//   if (ERPNEXT_API_KEY && ERPNEXT_API_SECRET) {
//     return {
//       'Content-Type': 'application/json',
//       Authorization: `token ${ERPNEXT_API_KEY}:${ERPNEXT_API_SECRET}`,
//     };
//   }
//
//   return {
//     'Content-Type': 'application/json',
//   };
// }

/**
 * Check stock availability for a specific item
 */
export async function checkStock(itemCode: string): Promise<{
  available: boolean;
  quantity: number;
}> {
  const quantity = await fetchStockLevel(itemCode);

  return {
    available: quantity > 0,
    quantity,
  };
}

/**
 * Get BOM (Bill of Materials) for a product
 * Useful for displaying "what's included" in the product
 */
export async function fetchBOM(itemCode: string): Promise<{
  components: Array<{ item: string; name: string; qty: number }>;
} | null> {
  try {
    const baseUrl = ERPNEXT_URL || '';
    const response = await fetch(
      `${baseUrl}/api/resource/BOM?` +
        new URLSearchParams({
          fields: JSON.stringify(['name', 'items']),
          filters: JSON.stringify([
            ['item', '=', itemCode],
            ['is_active', '=', 1],
            ['is_default', '=', 1],
          ]),
        }),
      {
        credentials: 'include',
      }
    );

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    const boms = data.data || [];

    if (boms.length === 0) {
      return null;
    }

    const bom = boms[0];

    // Fetch full BOM details
    const bomResponse = await fetch(`${baseUrl}/api/resource/BOM/${bom.name}`, {
      credentials: 'include',
    });

    if (!bomResponse.ok) {
      return null;
    }

    const bomData = await bomResponse.json();
    const components = (bomData.data.items || []).map((item: any) => ({
      item: item.item_code,
      name: item.item_name,
      qty: item.qty,
    }));

    return { components };
  } catch (error) {
    console.error(`Failed to fetch BOM for ${itemCode}:`, error);
    return null;
  }
}
